/**
 * SkillsDrawer.tsx
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — B5: skills management
 * as a right-side Drawer (replaces the stacked SkillsPanelDialog modal —
 * interaction cleanup only, same rules and service calls):
 *
 *   - search over name / category / subcategory
 *   - Category → Subcategory grouped list
 *   - inline add / rename (per-row save/cancel), active toggle
 *   - NO delete — skills can only be deactivated (backend returns 405);
 *     existing schedules keep resolving deactivated skills
 *
 * All API calls use scheduleTaxonomy (apiClient-backed). Theme tokens only.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton,
  InputAdornment, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import { Add, Close, Edit, PsychologyAlt, Search } from '@mui/icons-material';
import type { Skill } from '../../../types/schedule';
import { createSkill, fetchSkills, updateSkill } from '../../../services/scheduleTaxonomy';
import { SCHEDULE_CATEGORY_OPTIONS } from '../../../constants/scheduleEnums';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Refreshes the page's skill list (Required Skill pickers) after edits. */
  onChanged?: () => void;
}

interface SkillFormState {
  name: string;
  category: string;
  subcategory: string;
  sortOrder: string;
}

const EMPTY_FORM: SkillFormState = { name: '', category: '', subcategory: '', sortOrder: '0' };

interface SubGroup { subcategory: string; skills: Skill[] }
interface CatGroup { category: string; subGroups: SubGroup[] }

function groupSkills(skills: Skill[]): CatGroup[] {
  const catMap = new Map<string, Map<string, Skill[]>>();
  for (const s of skills) {
    const cat = s.Category ?? '(No Category)';
    const sub = s.Subcategory ?? '(No Subcategory)';
    if (!catMap.has(cat)) catMap.set(cat, new Map());
    const subMap = catMap.get(cat)!;
    if (!subMap.has(sub)) subMap.set(sub, []);
    subMap.get(sub)!.push(s);
  }
  return [...catMap.entries()].map(([category, subMap]) => ({
    category,
    subGroups: [...subMap.entries()].map(([subcategory, sks]) => ({ subcategory, skills: sks })),
  }));
}

export const SkillsDrawer: React.FC<Props> = ({ open, onClose, onChanged }) => {
  const theme = useTheme();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // null = no form; 'add' = adding; else = the SkillKey being edited.
  const [formMode, setFormMode] = useState<'add' | string | null>(null);
  const [form, setForm] = useState<SkillFormState>(EMPTY_FORM);

  const loadSkills = useCallback(async () => {
    setLoading(true);
    try {
      // Admin view: active + inactive so retired skills can be reactivated.
      setSkills(await fetchSkills(true));
    } catch {
      setErrorMsg('Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void loadSkills();
      setSearchQuery('');
      setFormMode(null);
      setForm(EMPTY_FORM);
      setErrorMsg(null);
    }
  }, [open, loadSkills]);

  const filteredSkills = useMemo(() => skills.filter((s) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      s.Name.toLowerCase().includes(q)
      || (s.Category ?? '').toLowerCase().includes(q)
      || (s.Subcategory ?? '').toLowerCase().includes(q)
    );
  }), [skills, searchQuery]);

  const grouped = useMemo(() => groupSkills(filteredSkills), [filteredSkills]);

  const handleStartAdd = () => {
    setFormMode('add');
    setForm(EMPTY_FORM);
    setErrorMsg(null);
  };

  const handleStartEdit = (skill: Skill) => {
    setFormMode(skill.SkillKey);
    setForm({
      name: skill.Name,
      category: skill.Category ?? '',
      subcategory: skill.Subcategory ?? '',
      sortOrder: String(skill.SortOrder ?? 0),
    });
    setErrorMsg(null);
  };

  const handleCancelForm = () => {
    setFormMode(null);
    setForm(EMPTY_FORM);
    setErrorMsg(null);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) { setErrorMsg('Name is required'); return; }
    if (name.length > 100) { setErrorMsg('Name must be 100 characters or fewer'); return; }
    setSaving(true);
    setErrorMsg(null);
    try {
      const payload = {
        Name: name,
        Category: form.category.trim() || undefined,
        Subcategory: form.subcategory.trim() || null,
        SortOrder: parseInt(form.sortOrder, 10) || 0,
      };
      if (formMode === 'add') await createSkill(payload);
      else await updateSkill(formMode as string, payload);
      handleCancelForm();
      await loadSkills();
      onChanged?.();
    } catch (err) {
      setErrorMsg(
        (err as { response?: { data?: { error?: string } } }).response?.data?.error
        ?? (err instanceof Error ? err.message : 'Failed to save'),
      );
    } finally {
      setSaving(false);
    }
  };

  /** Deactivate/reactivate — never delete (existing schedules depend on skills). */
  const handleToggleActive = async (skill: Skill) => {
    setSaving(true);
    setErrorMsg(null);
    try {
      await updateSkill(skill.SkillKey, { IsActive: !skill.IsActive });
      await loadSkills();
      onChanged?.();
    } catch (err) {
      setErrorMsg(
        (err as { response?: { data?: { error?: string } } }).response?.data?.error
        ?? (err instanceof Error ? err.message : 'Failed to update skill'),
      );
    } finally {
      setSaving(false);
    }
  };

  const renderForm = (mode: 'add' | string) => (
    <Stack
      spacing={1.5}
      sx={{
        p: 1.5,
        my: 0.5,
        borderRadius: 1.5,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <TextField
        size="small"
        label="Name"
        required
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        inputProps={{ maxLength: 100 }}
        autoFocus
        fullWidth
      />
      <Stack direction="row" spacing={1.5}>
        <TextField
          select
          size="small"
          label="Category"
          value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          fullWidth
        >
          <MenuItem value=""><em>None</em></MenuItem>
          {SCHEDULE_CATEGORY_OPTIONS.map((cat) => (
            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Subcategory"
          value={form.subcategory}
          onChange={(e) => setForm((f) => ({ ...f, subcategory: e.target.value }))}
          inputProps={{ maxLength: 100 }}
          fullWidth
        />
        <TextField
          size="small"
          label="Sort"
          type="number"
          value={form.sortOrder}
          onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
          inputProps={{ min: 0 }}
          sx={{ width: 90 }}
        />
      </Stack>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" onClick={handleCancelForm} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.name.trim()}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {mode === 'add' ? 'Add Skill' : 'Save'}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 480, maxWidth: '94vw' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 1.5 }}>
          <PsychologyAlt sx={{ color: theme.palette.primary.main }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            Manage Skills
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<Add />}
            onClick={handleStartAdd}
            disabled={formMode === 'add'}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add
          </Button>
          <IconButton size="small" onClick={onClose} aria-label="Close skills panel">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, pb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search skills by name, category, or subcategory…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Skills can be added, renamed, or deactivated — never deleted. Deactivating hides
            a skill from new pickers while existing schedules keep resolving it.
          </Typography>
        </Box>

        {errorMsg && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setErrorMsg(null)}>
            {errorMsg}
          </Alert>
        )}

        {formMode === 'add' && <Box sx={{ px: 2 }}>{renderForm('add')}</Box>}

        <Divider />

        {/* Grouped list */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {filteredSkills.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1 }}>
                  {searchQuery ? 'No skills match your search.' : 'No skills defined yet.'}
                </Typography>
              )}
              {grouped.map((catGroup) => (
                <Box key={catGroup.category} sx={{ mb: 1.5 }}>
                  <Typography
                    variant="overline"
                    sx={{ fontWeight: 700, color: theme.palette.primary.main, lineHeight: 1.6 }}
                  >
                    {catGroup.category}
                  </Typography>
                  {catGroup.subGroups.map((subGroup) => (
                    <Box key={subGroup.subcategory} sx={{ mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 600,
                          color: 'text.secondary',
                          letterSpacing: '0.04em',
                          pl: 1,
                          pb: 0.25,
                        }}
                      >
                        {subGroup.subcategory}
                      </Typography>
                      {subGroup.skills.map((skill) => (
                        formMode === skill.SkillKey ? (
                          <Box key={skill.SkillKey} sx={{ pl: 1 }}>{renderForm(skill.SkillKey)}</Box>
                        ) : (
                          <Box
                            key={skill.SkillKey}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              pl: 2,
                              pr: 0.5,
                              py: 0.4,
                              borderRadius: 1,
                              opacity: skill.IsActive ? 1 : 0.55,
                              '&:hover': { bgcolor: theme.table.hoverBg },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                color: skill.IsActive ? 'text.primary' : 'text.disabled',
                              }}
                              noWrap
                            >
                              {skill.Name}
                            </Typography>
                            {!skill.IsActive && (
                              <Chip label="Inactive" size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                            )}
                            <Tooltip
                              title={skill.IsActive
                                ? 'Deactivate (hides from new pickers; existing schedules keep resolving)'
                                : 'Reactivate (available in new pickers again)'}
                            >
                              <Switch
                                size="small"
                                checked={skill.IsActive}
                                onChange={() => void handleToggleActive(skill)}
                                disabled={saving || formMode !== null}
                                inputProps={{
                                  'aria-label': skill.IsActive
                                    ? `Deactivate ${skill.Name}`
                                    : `Activate ${skill.Name}`,
                                }}
                              />
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={() => handleStartEdit(skill)}
                              title="Rename skill"
                              disabled={saving || !skill.IsActive}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Box>
                        )
                      ))}
                    </Box>
                  ))}
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
